import { reactive } from 'vue';
import { supabase, useSupabase } from '../db/supabaseClient.js';
import { currentUser } from '../db/auth.js';

export const cartState = reactive({
  open: false,
  items: [], // { id, name, unit_price, qty, image_base64 }
});

// Helpers Supabase
async function getOrCreateActiveCartId() {
  if (!useSupabase()) return null;
  const user = currentUser();
  const userId = user?.id || null;
  if (userId === null) return null;
  // Buscar carrito activo del usuario
  let { data, error } = await supabase
    .from('carts')
    .select('id_carts, status, user_id')
    .eq('status', 'active')
    .eq('user_id', userId)
    .limit(1);
  if (error) {
    console.warn('Supabase carts select error:', error?.message || error);
    return null;
  }
  if (data && data.length) return data[0].id_carts;
  // Crear carrito activo
  const ins = await supabase
    .from('carts')
    .insert({ user_id: userId, status: 'active' })
    .select('id_carts')
    .single();
  if (ins.error) {
    console.warn('Supabase carts insert error:', ins.error?.message || ins.error);
    return null;
  }
  return ins.data?.id_carts || null;
}

async function getOrCreateCustomerIdForCurrentUser() {
  const u = currentUser();
  const name = u?.full_name || u?.username || null;
  const email = u?.email || null;
  if (!useSupabase() || (!name && !email)) return null;
  // Buscar cliente por email primero, luego por nombre
  let { data: rows } = await supabase
    .from('customers')
    .select('id_customers')
    .or([
      email ? `email.eq.${email}` : '',
      name ? `name.eq.${name}` : '',
    ].filter(Boolean).join(','))
    .limit(1);
  const found = rows && rows[0]?.id_customers;
  if (found) return found;
  const { data: created, error } = await supabase
    .from('customers')
    .insert({ name: name || 'Cliente', email: email || null })
    .select('id_customers')
    .single();
  if (error) return null;
  return created?.id_customers || null;
}

async function loadCartFromSupabase() {
  if (!useSupabase()) return;
  const cartId = await getOrCreateActiveCartId();
  if (!cartId) return;
  const { data: rows, error } = await supabase
    .from('cart_items')
    .select('product_id, quantity, unit_price')
    .eq('cart_id', cartId);
  if (error) {
    console.warn('Supabase cart_items select error:', error?.message || error);
    return;
  }
  const items = rows || [];
  const ids = items.map(r => r.product_id);
  let detailsMap = new Map();
  if (ids.length) {
    const { data: details, error: derr } = await supabase
      .from('v_inventory_basic')
      .select('id, name, image_base64, image_mime')
      .in('id', ids);
    if (!derr && Array.isArray(details)) {
      details.forEach(d => detailsMap.set(d.id, d));
    }
  }
  cartState.items = items.map(r => {
    const d = detailsMap.get(r.product_id) || {};
    return {
      id: r.product_id,
      name: d.name || '',
      unit_price: Number(r.unit_price || 0),
      qty: Number(r.quantity || 1),
      image_base64: d.image_base64 || null,
      image_mime: d.image_mime || null,
    };
  });
}

export async function syncCartFromSupabase() {
  await loadCartFromSupabase();
}

export async function addToCart(product, qty = 1) {
  if (!product || !product.id) return;
  const existing = cartState.items.find(i => i.id === product.id);
  if (existing) {
    existing.qty += qty;
    // Asegurar que el precio se actualice (por ejemplo, si se aplica descuento desde la modal)
    const incoming = Number(product.unit_price ?? existing.unit_price ?? 0);
    if (!Number.isNaN(incoming) && incoming > 0) {
      existing.unit_price = incoming;
    }
  } else {
    cartState.items.push({
      id: product.id,
      name: product.name,
      unit_price: Number(product.unit_price || 0),
      qty: Number(qty || 1),
      image_base64: product.image_base64 || null,
    });
  }

  // Persistir en Supabase
  if (useSupabase()) {
    const cartId = await getOrCreateActiveCartId();
    if (!cartId) return;
    // Buscar item existente en DB
    const { data: rows, error } = await supabase
      .from('cart_items')
      .select('id_cart_items, quantity, unit_price')
      .eq('cart_id', cartId)
      .eq('product_id', product.id)
      .limit(1);
    if (!error && rows && rows.length) {
      const row = rows[0];
      const newQty = Number(row.quantity || 0) + Number(qty || 1);
      const newPrice = Number(product.unit_price ?? row.unit_price ?? 0);
      await supabase
        .from('cart_items')
        .update({ quantity: newQty, unit_price: newPrice })
        .eq('id_cart_items', row.id_cart_items);
    } else {
      await supabase
        .from('cart_items')
        .insert({
          cart_id: cartId,
          product_id: product.id,
          quantity: Number(qty || 1),
          unit_price: Number(product.unit_price || 0),
          discount: 0,
        });
    }
    await loadCartFromSupabase();
  }
}

export async function removeFromCart(productId) {
  cartState.items = cartState.items.filter(i => i.id !== productId);
  if (useSupabase()) {
    const cartId = await getOrCreateActiveCartId();
    if (!cartId) return;
    await supabase
      .from('cart_items')
      .delete()
      .eq('cart_id', cartId)
      .eq('product_id', productId);
    await loadCartFromSupabase();
  }
}

export async function setItemQty(productId, qty) {
  const it = cartState.items.find(i => i.id === productId);
  if (!it) return;
  const q = Math.max(1, Number(qty || 1));
  it.qty = q;
  if (useSupabase()) {
    const cartId = await getOrCreateActiveCartId();
    if (!cartId) return;
    await supabase
      .from('cart_items')
      .update({ quantity: q })
      .eq('cart_id', cartId)
      .eq('product_id', productId);
    await loadCartFromSupabase();
  }
}

export async function incrementItem(productId) {
  const it = cartState.items.find(i => i.id === productId);
  if (it) it.qty += 1;
  if (useSupabase()) {
    await setItemQty(productId, it?.qty || 1);
  }
}

export async function decrementItem(productId) {
  const it = cartState.items.find(i => i.id === productId);
  if (it) it.qty = Math.max(1, it.qty - 1);
  if (useSupabase()) {
    await setItemQty(productId, it?.qty || 1);
  }
}

export async function clearCart() {
  cartState.items = [];
  if (useSupabase()) {
    const cartId = await getOrCreateActiveCartId();
    if (!cartId) return;
    await supabase
      .from('cart_items')
      .delete()
      .eq('cart_id', cartId);
    await loadCartFromSupabase();
  }
}

export function cartTotal() {
  return cartState.items.reduce((sum, i) => sum + (i.unit_price * i.qty), 0);
}

export function cartCount() {
  return cartState.items.reduce((sum, i) => sum + i.qty, 0);
}

export async function checkoutCart() {
  if (!useSupabase()) {
    // En modo local solo limpiamos y cerramos
    const total = cartTotal();
    cartState.items = [];
    cartState.open = false;
    return { ok: true, total };
  }
  const cartId = await getOrCreateActiveCartId();
  if (!cartId) return { ok: false, message: 'No se pudo iniciar carrito' };
  // Leer total desde la vista para confirmar
  const { data: totals } = await supabase
    .from('v_cart_totals')
    .select('total, items_count')
    .eq('cart_id', cartId)
    .limit(1);
  const total = totals && totals.length ? Number(totals[0].total || 0) : cartTotal();
  // Marcar carrito como enviado
  await supabase
    .from('carts')
    .update({ status: 'submitted' })
    .eq('id_carts', cartId);
  // Crear un nuevo carrito activo automáticamente
  const uid = currentUser()?.id || null;
  if (uid !== null) {
    await supabase
      .from('carts')
      .insert({ user_id: uid, status: 'active' });
  }
  cartState.items = [];
  cartState.open = false;
  return { ok: true, total };
}

export async function finalizeSale({ payment_method = 'cash', amount_received, change } = {}) {
  const itemsList = cartState.items.slice();
  const subtotal = cartState.items.reduce((sum, i) => sum + (Number(i.unit_price || 0) * Number(i.qty || 0)), 0);
  const discount_total = 0;
  const tax_total = 0;
  const total = Number((subtotal - discount_total + tax_total).toFixed(2));
  if (!useSupabase()) {
    cartState.items = [];
    cartState.open = false;
    return { ok: true, total };
  }
  const customer_id = await getOrCreateCustomerIdForCurrentUser();
  const { data: saleRow, error: saleErr } = await supabase
    .from('sales')
    .insert({
      customer_id: customer_id ?? null,
      doc_number: null,
      payment_method: String(payment_method),
      notes: amount_received !== undefined ? `cash_received=${Number(amount_received).toFixed(2)}; change=${Number(change || 0).toFixed(2)}` : null,
      subtotal: Number(subtotal.toFixed(2)),
      discount_total: Number(discount_total.toFixed(2)),
      tax_total: Number(tax_total.toFixed(2)),
      total: Number(total.toFixed(2)),
    })
    .select('id_sales')
    .single();
  if (saleErr) return { ok: false, message: saleErr.message };
  const saleId = saleRow?.id_sales;
  if (!saleId) return { ok: false, message: 'No se pudo crear la venta' };
  const itemsPayload = cartState.items.map((it) => ({
    sale_id: saleId,
    product_id: it.id,
    quantity: Number(it.qty || 0),
    unit_price: Number(it.unit_price || 0),
    discount: 0,
    tax_rate: 0,
    total: Number((Number(it.unit_price || 0) * Number(it.qty || 0)).toFixed(2)),
  }));
  if (itemsPayload.length) {
    const { error: itemsErr } = await supabase.from('sale_items').insert(itemsPayload);
    if (itemsErr) return { ok: false, message: itemsErr.message };
  }
  await clearCart();
  cartState.open = false;
  return { ok: true, total, sale_id: saleId, items: itemsList, payment_method, amount_received, change };
}